import { PermissionRequestResult, TransferDetailsFields } from '@Types'
import isNil from 'lodash.isnil'
import {
  ACCOUNTS_VIEW,
  ACH_TRANSFERS,
  CASH_JOURNAL_TRANSFERS,
  POSITION_JOURNAL_TRANSFERS,
  ACH_UI_VIEW,
  WIRE_UI_VIEW,
  CHECK_UI_VIEW,
  CHECK_TRANSFERS,
  CHECK_OVERNIGHT_TO_APEX,
  CASH_JOURNAL_UI_VIEW,
  CASH_TRANSFER_POSITION_JOURNAL_UI_VIEW,
  WIRE_TRANSFERS,
  SCHEDULE_TRANSFERS_UPDATE,
  SCHEDULE_TRANSFERS_CANCEL,
  SCHEDULE_TRANSFERS_READ,
  POSITION_JOURNAL_UI_VIEW,
  POSITION_JOURNAL_READ,
  POSITION_JOURNAL_REWARD,
  RTP_TRANSFER_DEPOSIT_UPDATE,
  RTP_TRANSFER_WITHDRAWAL_UPDATE
} from '../constants'
import { ACH_VALUE } from '../utils/optionBuilders'

export const hasAllPermissions = (
  permissions: PermissionRequestResult[] | null | undefined,
  expectedPermissions: string[]
) => {
  const uniquePermissions: Set<string> = new Set<string>()
  if (isNil(permissions)) {
    return false
  }
  // Get the unique permission set, in case the set has the same permission twice
  permissions.forEach((perm) => {
    if (perm?.permitted) {
      const permission: string = perm?.request?.permission
      uniquePermissions.add(permission)
    }
  })
  const missingPerms = expectedPermissions.filter((expected) => {
    return !uniquePermissions.has(expected)
  })
  return missingPerms.length === 0
}
// TODO: Possibly Remove in the future.
export const hasViewTransfersAccess = (userPerms: PermissionRequestResult[] | null | undefined) => {
  const expectedBasicPerms: string[] = [...ACCOUNTS_VIEW]
  return hasAllPermissions(userPerms, expectedBasicPerms)
}

export const hasAchTransfersAccess = (userPerms: PermissionRequestResult[] | null | undefined) =>
  hasAllPermissions(userPerms, ACH_TRANSFERS) && hasAchUiPermissions(userPerms)

export const hasWireTransfersAccess = (userPerms: PermissionRequestResult[] | null | undefined) =>
  hasAllPermissions(userPerms, WIRE_TRANSFERS) && hasWireUiPermissions(userPerms)

export const hasCheckTransfersAccess = (userPerms: PermissionRequestResult[] | null | undefined) =>
  hasAllPermissions(userPerms, CHECK_TRANSFERS) && hasCheckUiPermissions(userPerms)

export const hasPositionJournalTransfersAccess = (userPerms: PermissionRequestResult[] | null | undefined) =>
  hasViewTransfersAccess(userPerms) && hasAllPermissions(userPerms, POSITION_JOURNAL_TRANSFERS)

export const hasAnyTransfersAccess = (userPerms?: PermissionRequestResult[] | null) => hasAchTransfersAccess(userPerms)

export const hasSpecificTransferPermissions = (
  transferDetails: TransferDetailsFields | undefined,
  userPerms: PermissionRequestResult[] | null | undefined
) => {
  const transferType = transferDetails?.transferType?.raw
  if (isNil(transferType)) {
    return false
  }

  switch (transferType) {
    case ACH_VALUE:
      return hasAchTransfersAccess(userPerms)
    default:
      return false
  }
}

export const hasCashJournalApiPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, [...CASH_JOURNAL_TRANSFERS, ...ACCOUNTS_VIEW, ...CASH_JOURNAL_UI_VIEW])
}
export const hasCashJournalUiPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, CASH_JOURNAL_UI_VIEW)
}

export const hasPositionJournalApiPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, [...POSITION_JOURNAL_TRANSFERS])
}
export const hasAchUiPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, ACH_UI_VIEW)
}
export const hasPositionJournalUiPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, POSITION_JOURNAL_UI_VIEW)
}
export const hasWireUiPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, WIRE_UI_VIEW)
}
export const hasCheckUiPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, CHECK_UI_VIEW)
}
export const hasCheckOvernightToApexPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, CHECK_OVERNIGHT_TO_APEX)
}

export const hasScheduleTransferReadPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, SCHEDULE_TRANSFERS_READ)
}
export const hasScheduleTransferUpdatePermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, SCHEDULE_TRANSFERS_UPDATE)
}
export const hasScheduleTransferCancelPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, SCHEDULE_TRANSFERS_CANCEL)
}

export const hasCashTransferPositionJournalReadPermissions = (
  userPerms: PermissionRequestResult[] | null | undefined
) => {
  return hasAllPermissions(userPerms, CASH_TRANSFER_POSITION_JOURNAL_UI_VIEW)
}
export const hasCashTransferPositionJournalUpdatePermissions = (
  userPerms: PermissionRequestResult[] | null | undefined
) => {
  return hasAllPermissions(userPerms, [...POSITION_JOURNAL_READ, ...POSITION_JOURNAL_REWARD])
}

export const hasRtpDepositCancelPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, RTP_TRANSFER_DEPOSIT_UPDATE)
}

export const hasRtpWithdrawalCancelPermissions = (userPerms: PermissionRequestResult[] | null | undefined) => {
  return hasAllPermissions(userPerms, RTP_TRANSFER_WITHDRAWAL_UPDATE)
}
